import omit from 'lodash/omit';
import pick from 'lodash/pick';
import omitBy from 'lodash/omitBy';
import isUndefined from 'lodash/isUndefined';
import isObject from 'lodash/isObject';

import {
  FUNNELS_MATH_TYPES,
  NUMERIC_MATH_TYPES,
  PER_USER_AGGREGATIONS,
  PRIMITIVE_MATH_TYPES,
  RETENTION_MATH_TYPES,
  SESSION_EVENT_NAMES,
  SESSION_EVENTS,
  XAU_MATH_TYPES,
} from 'common/report/constants';
import {MetricType} from 'common/query-builder/query-block/constants';

import {
  BehaviorMetric,
  BehaviorShowClause as BehaviorShowClauseType,
  FormulaShowClause as FormulaShowClauseType,
  InlineFormula,
  InsightsResourceType,
  ShowClause as ShowClauseType,
} from '../util/types';
import {DATASET_MIXPANEL} from './constants';
import {isFormulaShowClause, isValidFormula, sanitizeIfCustomProperty, transformShowToBehaviorMetric} from './utils';
import {FilterClause} from './filter-clause';
import {ClauseType} from './types';

export abstract class ShowClause {
  static TYPE: typeof ClauseType.Show = ClauseType.Show;

  static createShowClause(attrs: ShowClauseType): ShowClause {
    if (isFormulaShowClause(attrs)) {
      return new FormulaShowClause(attrs);
    } else {
      return new BehaviorShowClause(attrs);
    }
  }

  abstract get attrs(): ShowClauseType;

  abstract get valid(): boolean;

  serialize(): ShowClauseType {
    return this.attrs;
  }

  abstract extend(attrs: Partial<ShowClauseType>): ShowClause;

  abstract toBookmarkData(): ShowClauseType;

  abstract toUrlData(params?: {expandSessions?: boolean}): ShowClauseType;
}

/**
 * This union type fixes the following problem with using class ShowClause as the type:
 *
 *   const showClause: ShowClause = ...;
 *   if (showClause instanceof BehaviorShowClause) {
 *     ...
 *   } else {
 *     // showClause's type here is `ShowClause`
 *     if (showClause instanceof FormulaShowClause) {
 *       // so `instanceof FormulaShowClause` needed to narrow type
 *     }
 *   }
 *
 * But if showClause is typed as ShowClauseClassType:
 *
 *   const showClause: ShowClauseClassType = ...;
 *   if (showClause instanceof BehaviorShowClause) {
 *     ...
 *   } else {
 *     // showClause's type here is `FormulaShowClause`
 *   }
 */
export type ShowClauseClassType = BehaviorShowClause | FormulaShowClause;

export class BehaviorShowClause extends ShowClause {
  behavior: BehaviorMetric['behavior'];
  measurement: BehaviorMetric['measurement'];

  // eslint-disable-next-line sonarjs/cognitive-complexity
  constructor(attrs: BehaviorShowClauseType) {
    super();
    const {behavior, measurement} = transformShowToBehaviorMetric(attrs);

    this.behavior = behavior;
    this.measurement = measurement;
    this.behavior.dataset = behavior.dataset || null;
    this.behavior.dataset = behavior.dataset === `mixpanel` ? DATASET_MIXPANEL : behavior.dataset;
    this.behavior.value = behavior.value || null;
    this.behavior.resourceType = behavior.resourceType || InsightsResourceType.Events;
    this.behavior.profileType = behavior.profileType || null;
    this.behavior.search = behavior.search || ``;
    this.behavior.dataGroupId = behavior.dataGroupId || null;

    if (behavior.type) {
      this.behavior.type = behavior.type;
    }
    if (behavior.customBucket) {
      this.behavior.customBucket = behavior.customBucket;
    }

    if (this.behavior.resourceType === InsightsResourceType.People) {
      this.behavior.profileType = InsightsResourceType.People;
    }

    if (this.behavior.value && this.behavior.value.session_event) {
      // If this is a session event formatted for querying, convert it to the UI format
      // (e.g. from {session_event: 'start'} to {name: '$session_start'})
      this.behavior.value = {
        dataset: DATASET_MIXPANEL,
        name: SESSION_EVENT_NAMES[this.behavior.value.session_event],
        // session_event is still being used by qapi for getting the session event correctly
        session_event: this.behavior.value.session_event,
        ...(this.behavior.value.renamed && {renamed: this.behavior.value.renamed}),
      };
    }

    if (behavior.filter && behavior.filter.length) {
      this.behavior.filterDeterminer = behavior.filterDeterminer;
      this.behavior.filter = behavior.filter;
    }

    if (behavior.behaviors && behavior.behaviors.length) {
      this.behavior.behaviors = behavior.behaviors;
    }

    if (behavior.conversionWindowDuration) {
      this.behavior.conversionWindowDuration = behavior.conversionWindowDuration;
    }
    if (behavior.conversionWindowUnit) {
      this.behavior.conversionWindowUnit = behavior.conversionWindowUnit;
    }
    if (behavior.funnelOrder) {
      this.behavior.funnelOrder = behavior.funnelOrder;
    }
    if (behavior.exclusions) {
      this.behavior.exclusions = behavior.exclusions;
    }
    if (behavior.aggregateBy) {
      this.behavior.aggregateBy = behavior.aggregateBy;
    }

    // Retention specific fields
    if (behavior.retentionType) {
      this.behavior.retentionType = behavior.retentionType;
    }
    if (behavior.retentionAlignmentType) {
      this.behavior.retentionAlignmentType = behavior.retentionAlignmentType;
    }
    if (behavior.retentionUnit) {
      this.behavior.retentionUnit = behavior.retentionUnit;
    }
    if (behavior.retentionUnbounded) {
      this.behavior.retentionUnbounded = behavior.retentionUnbounded;
    }
    if (behavior.retentionCustomBucketSizes) {
      this.behavior.retentionCustomBucketSizes = behavior.retentionCustomBucketSizes;
    }

    const defaultMathType =
      this.behavior.type === MetricType.Retention ? RETENTION_MATH_TYPES.RETENTION_RATE : PRIMITIVE_MATH_TYPES.TOTAL;
    this.measurement.math = measurement.math || defaultMathType;
    this.measurement.perUserAggregation = measurement.perUserAggregation || null;
    this.measurement.rolling = measurement.rolling || null;
    this.measurement.cumulative = measurement.cumulative || false;
    // TODO: Move sanitization logic app api bookmark endpoint
    this.measurement.property = measurement.property
      ? omit(sanitizeIfCustomProperty(measurement.property), `label`)
      : null;

    if (measurement.multiAttribution) {
      this.measurement.multiAttribution = measurement.multiAttribution;
    }

    // Funnels specific fields
    if (measurement.stepIndex) {
      this.measurement.stepIndex = measurement.stepIndex;
    }

    // Retention specific fields
    if (measurement.retentionBucketIndex) {
      this.measurement.retentionBucketIndex = measurement.retentionBucketIndex;
    }
  }

  serializeFilter() {
    if (this.behavior.filter && this.behavior.filter.length) {
      const filters = this.behavior.filter.map((clause, index) => ({
        ...new FilterClause(clause).serialize(),
        // Assign determiner to the first filter
        ...(index === 0 ? {determiner: this.behavior.filterDeterminer} : {}),
      }));

      return {filter: filters};
    } else {
      return {};
    }
  }

  getInlineCohort() {
    return this.behavior.value && this.behavior.value.raw_cohort ? this.behavior.value.raw_cohort : null;
  }

  get isInlineCohort() {
    return !!this.getInlineCohort();
  }

  get attrs(): BehaviorShowClauseType {
    return {
      // TODO: fix typing so that as BehaviorMetric[...] is not needed
      behavior: omitBy(
        {
          ...this.behavior,
          ...this.serializeFilter(),
        },
        isUndefined,
      ) as BehaviorMetric['behavior'],
      measurement: omitBy(
        {
          ...this.measurement,
        },
        isUndefined,
      ) as BehaviorMetric['measurement'],
    };
  }

  get customEventId() {
    return this.behavior.value.custom && !this.behavior.value.is_collect_everything_event
      ? this.behavior.value.id
      : null;
  }

  get valid(): boolean {
    const isValidValue =
      this.behavior.type === MetricType.Funnel
        ? true
        : !this.behavior.value || !(isObject(this.behavior.value) && Object.keys(this.behavior.value).length === 0); // don't allow empty object as value
    const isValidMathType = [
      ...Object.values(PRIMITIVE_MATH_TYPES),
      ...Object.values(NUMERIC_MATH_TYPES),
      ...Object.values(XAU_MATH_TYPES),
      ...Object.values(PER_USER_AGGREGATIONS),
      ...Object.values(FUNNELS_MATH_TYPES),
      ...Object.values(RETENTION_MATH_TYPES),
    ].includes(this.measurement.math);
    return isValidValue && isValidMathType;
  }

  extend(attrs: Partial<BehaviorShowClauseType> = {}): BehaviorShowClause {
    const {behavior, measurement} = transformShowToBehaviorMetric(attrs);
    return new BehaviorShowClause({
      behavior: {
        ...this.behavior,
        ...omitBy(behavior, isUndefined),
        type: this.behavior.type,
      },
      measurement: {
        ...this.measurement,
        ...omitBy(measurement, isUndefined),
      },
    });
  }

  toBookmarkData(): ShowClauseType {
    const attrs = this.attrs;
    return {
      behavior: {
        ...attrs.behavior,
        value: this._pickValueEntries({extraFields: [`resourceType`, `renamed`], expandSessions: true}),
      },
      measurement: attrs.measurement,
    };
  }

  isXauClause(): boolean {
    return Object.values(XAU_MATH_TYPES).includes(this.measurement.math as XAU_MATH_TYPES);
  }

  isRollingClause(): boolean {
    return !!this.measurement.rolling;
  }

  isCumulativeClause(): boolean {
    return !!this.measurement.cumulative;
  }

  _pickValueEntries({
    extraFields = [],
    expandSessions,
  }: {extraFields?: string[]; expandSessions?: boolean} = {}): BehaviorMetric['behavior']['value'] {
    let value = this.behavior.value || {};
    let valueFields = [];
    if (value.resourceType === InsightsResourceType.Cohorts) {
      valueFields = [`id`, `name`, `raw_cohort`];
    } else if (value.custom) {
      valueFields = [`id`, `custom`];
    } else {
      valueFields = [`name`];
    }

    // Support for sessions
    if (expandSessions && SESSION_EVENTS[value.name]) {
      value = {
        ...value,
        ...SESSION_EVENTS[value.name],
      };
      valueFields = [...valueFields, `session_event`];
    }
    return pick(value, [...valueFields, ...extraFields]);
  }

  toUrlData({expandSessions}: {expandSessions?: boolean} = {}): BehaviorShowClauseType {
    const attrs = this.attrs;
    const {type: _type, ...behavior} = attrs.behavior;
    return {
      ...behavior,
      ...attrs.measurement,
      value: this._pickValueEntries({extraFields: [`resourceType`, `renamed`], expandSessions}),
    };
  }
}

export class FormulaShowClause extends ShowClause {
  formula: InlineFormula;

  constructor(attrs: FormulaShowClauseType) {
    super();
    this.formula = attrs.formula;
  }

  get attrs(): FormulaShowClauseType {
    return this.serializeFormula();
  }

  serializeFormula(): FormulaShowClauseType {
    return {
      formula: {...this.formula},
    };
  }

  get valid(): boolean {
    return isValidFormula(this.formula);
  }

  extend(attrs: Partial<FormulaShowClauseType> = {}): FormulaShowClause {
    return new FormulaShowClause({
      formula: {
        ...this.formula,
        ...attrs.formula,
      },
    });
  }

  toBookmarkData(): FormulaShowClauseType {
    return this.serializeFormula();
  }

  toUrlData(): FormulaShowClauseType {
    return this.serializeFormula();
  }
}
